import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  VideoLibrary,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { URLMediaCache } from '../../services/cache/URLMediaCache';
import { useCachedVideo } from '../../hooks/useURLMediaCache';

interface VideoReviewPlayerProps {
  interviewId: string;
  candidateName: string;
  duration: number;
}

export interface VideoReviewPlayerRef {
  seekToTime: (seconds: number) => Promise<boolean>;
  getCurrentTime: () => number;
  isVideoLoaded: () => boolean;
  getInterviewStartTime: () => string | null;
  play: () => Promise<boolean>;
  pause: () => boolean;
  scrollIntoView: () => void;
}

const VideoReviewPlayer = forwardRef<VideoReviewPlayerRef, VideoReviewPlayerProps>(({
  interviewId,
  candidateName,
  duration,
}, ref) => {
  const { user } = useAuth();
  const [fullVideoUrl, setFullVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [privacyExpired, setPrivacyExpired] = useState(false);
  const [interviewStartTime, setInterviewStartTime] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Use URL-based caching for the video (transparent to styling)
  const { 
    mediaUrl: cachedVideoUrl, 
    isLoading: isCacheLoading, 
    error: cacheError, 
    isCached 
  } = useCachedVideo(fullVideoUrl || undefined, {
    autoCache: true, // Enable URL-based caching (no CORS issues)
    cacheTimeout: 10000 // 10 second timeout for cache operations
  });

  // Initialize URLMediaCache when user is available
  useEffect(() => {
    if (user?.email) {
      URLMediaCache.initialize(user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    loadVideoData();
  }, [interviewId]);

  // Expose video seeking methods to parent components
  useImperativeHandle(ref, () => ({
    seekToTime: async (seconds: number): Promise<boolean> => {
      const effectiveVideoUrl = cachedVideoUrl || fullVideoUrl;
      if (!videoRef.current || !effectiveVideoUrl) {
        console.warn('[VideoReviewPlayer] Cannot seek: video not loaded', {
          hasVideoRef: !!videoRef.current,
          hasCachedUrl: !!cachedVideoUrl,
          hasFullUrl: !!fullVideoUrl
        });
        return false;
      }

      try {
        const video = videoRef.current;
        
        // Check if video metadata is loaded (readyState >= 1)
        if (video.readyState < 1) {
          console.log('[VideoReviewPlayer] Waiting for video metadata to load...');
          
          // Wait for loadedmetadata event with timeout
          const metadataLoaded = await new Promise<boolean>((resolve) => {
            const timeout = setTimeout(() => {
              console.warn('[VideoReviewPlayer] Video metadata load timeout');
              resolve(false);
            }, 5000); // 5 second timeout
            
            const onMetadataLoaded = () => {
              clearTimeout(timeout);
              video.removeEventListener('loadedmetadata', onMetadataLoaded);
              resolve(true);
            };
            
            video.addEventListener('loadedmetadata', onMetadataLoaded);
            
            // Check again in case it loaded between checks
            if (video.readyState >= 1) {
              clearTimeout(timeout);
              video.removeEventListener('loadedmetadata', onMetadataLoaded);
              resolve(true);
            }
          });
          
          if (!metadataLoaded) {
            console.error('[VideoReviewPlayer] Video metadata failed to load within timeout');
            return false;
          }
        }
        
        // Validate seek position is within video duration
        if (seconds > video.duration) {
          console.warn('[VideoReviewPlayer] Seek position beyond video duration:', {
            seekTime: seconds,
            duration: video.duration
          });
          // Seek to end instead of failing
          video.currentTime = video.duration - 1;
        } else if (seconds < 0) {
          console.warn('[VideoReviewPlayer] Negative seek position, seeking to start');
          video.currentTime = 0;
        } else {
          video.currentTime = seconds;
        }
        
        console.log(`[VideoReviewPlayer] Successfully seeked to ${video.currentTime} seconds`);
        return true;
      } catch (error) {
        console.error('[VideoReviewPlayer] Error seeking video:', error);
        return false;
      }
    },

    getCurrentTime: (): number => {
      if (!videoRef.current) {
        return 0;
      }
      return videoRef.current.currentTime;
    },

    isVideoLoaded: (): boolean => {
      // Video is loaded if we have either the cached URL or original URL, and video element exists
      const effectiveVideoUrl = cachedVideoUrl || fullVideoUrl;
      return !!effectiveVideoUrl && !!videoRef.current;
    },

    getInterviewStartTime: (): string | null => {
      return interviewStartTime;
    },

    play: async (): Promise<boolean> => {
      if (!videoRef.current) {
        console.warn('[VideoReviewPlayer] Cannot play: video not loaded');
        return false;
      }
      
      try {
        await videoRef.current.play();
        console.log('[VideoReviewPlayer] Video started playing');
        return true;
      } catch (error) {
        console.error('[VideoReviewPlayer] Error playing video:', error);
        return false;
      }
    },

    pause: (): boolean => {
      if (!videoRef.current) {
        console.warn('[VideoReviewPlayer] Cannot pause: video not loaded');
        return false;
      }
      
      try {
        videoRef.current.pause();
        console.log('[VideoReviewPlayer] Video paused');
        return true;
      } catch (error) {
        console.error('[VideoReviewPlayer] Error pausing video:', error);
        return false;
      }
    },

    scrollIntoView: (): void => {
      // Find the video player container (the Paper element)
      const videoContainer = videoRef.current?.closest('[data-video-container]');
      if (videoContainer) {
        videoContainer.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        console.log('[VideoReviewPlayer] Scrolled video into center view');
      } else {
        console.warn('[VideoReviewPlayer] Could not find video container for scrolling');
      }
    }
  }), [cachedVideoUrl, fullVideoUrl, interviewStartTime]);

  const loadVideoData = async () => {
    try {
      setLoading(true);
      
      // Get interview results for start time and privacy status
      const { resultsAPI } = await import('../../services/apiService');
      const interviewResults = await resultsAPI.getInterviewResults(interviewId);
      
      // Store interview start time for timestamp calculations
      const startTime = interviewResults.interview.startedAt;
      
      // Validate that start time is a valid date string
      if (startTime && !isNaN(new Date(startTime).getTime())) {
        setInterviewStartTime(startTime);
        console.log('[VideoReviewPlayer] Interview start time:', startTime);
      } else {
        console.error('[VideoReviewPlayer] Invalid interview start time format:', startTime);
        setInterviewStartTime(null);
      }
      
      // Check if privacy has expired (data automatically deleted for compliance)
      // Privacy is expired only if there's an actual expiration date
      const hasPrivacyExpired = !!interviewResults.interview.privacy_expired_at;
      setPrivacyExpired(hasPrivacyExpired);
      
      if (hasPrivacyExpired) {
        console.log('Privacy expired for interview:', interviewId, 'at:', interviewResults.interview.privacy_expired_at);
        setFullVideoUrl(null);
      } else {
        // Get video from infractions API instead of interview results
        const { infractionsAPI } = await import('../../services/apiService');
        const infractions = await infractionsAPI.getInfractions(interviewId);
        
        // Find the video recording in infractions (handle both new and old types)
        const videoInfraction = infractions.find(inf => 
          inf.evidence_type === 'video' && 
          (inf.type === 'video_recording' || 
           inf.description?.includes('Final interview video') ||
           (inf.type === 'screenshot_upload' && inf.description?.includes('Final interview video'))) // Backward compatibility
        );
        
        if (videoInfraction) {
          console.log('[VideoReviewPlayer] Found video in infractions:', {
            sk: videoInfraction.sk,
            type: videoInfraction.type,
            evidence_type: videoInfraction.evidence_type,
            r2_key: videoInfraction.evidence_r2_key
          });
          
          // Get signed URL for video from infractions API (same pattern as screenshots)
          try {
            const evidenceResponse = await infractionsAPI.getInfractionEvidence(videoInfraction.sk, interviewId);
            if (evidenceResponse.evidence_url) {
              console.log('[VideoReviewPlayer] Got signed video URL:', evidenceResponse.evidence_url);
              setFullVideoUrl(evidenceResponse.evidence_url);
            } else {
              console.error('[VideoReviewPlayer] No evidence URL in response:', evidenceResponse);
              setFullVideoUrl(null);
            }
          } catch (evidenceError) {
            console.error('[VideoReviewPlayer] Failed to get video evidence URL:', evidenceError);
            setFullVideoUrl(null);
          }
        } else {
          console.log('[VideoReviewPlayer] No video found in infractions for interview:', interviewId);
          console.log('[VideoReviewPlayer] Available infractions:', infractions.map(inf => ({
            type: inf.type,
            evidence_type: inf.evidence_type,
            description: inf.description
          })));
          setFullVideoUrl(null);
        }
      }
      
      setLoading(false);
      
    } catch (err) {
      console.error('Failed to load video data:', err);
      setError('Failed to load interview video data');
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show loading if either API is loading or cache is loading (but we have a URL to cache)
  if (loading || (fullVideoUrl && isCacheLoading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {loading ? 'Loading interview video...' : 'Preparing video from cache...'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={2} sx={{ p: 3 }} data-video-container>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <VideoLibrary sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Full Interview Recording
          </Typography>
          <Chip 
            label={formatTime(duration)}
            size="small"
            sx={{ ml: 2 }}
          />
        </Box>

        {fullVideoUrl ? (
          <Box sx={{ 
            position: 'relative',
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden',
            borderRadius: 1,
            bgcolor: 'grey.100'
          }}>
            <video
              ref={videoRef}
              controls
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            >
              {/* Always use original URL - caching happens transparently via browser */}
              <source src={fullVideoUrl} type="video/webm" />
              Your browser does not support the video tag.
            </video>
          </Box>
        ) : privacyExpired ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Media Automatically Deleted for Privacy Compliance
            </Typography>
            <Typography variant="body2">
              Video recordings and screenshots for this interview have been automatically 
              deleted after 30 days in accordance with our privacy policy. Interview metadata, 
              answers, and analytics remain available.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Video Recording Coming Soon
            </Typography>
            <Typography variant="body2">
              Full interview recording functionality is being implemented. 
              This will show the complete screen recording of {candidateName}'s interview session.
            </Typography>
          </Alert>
        )}
      </Paper>
    </Box>
  );
});

export default VideoReviewPlayer;